import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { MaskProps, useMask } from '@react-input/mask';
import { InputAdornment } from '@mui/material';
import { TextFieldProps } from '@mui/material/TextField';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { InputType } from '@/components/form/uncontrolled/Input/enums';
import {
  alphanumericRegExp,
  alphanumericRegExpAndSpace,
  commonInputValidationPattern,
  decimalRegExp,
  integerValidationPattern,
  lettersAndSpacesRegExp,
  lettersRegExp,
  numberValidationPattern,
  phoneValidationPattern,
  ssnValidationPattern,
  tinValidationPattern,
} from '@/constants/regExp';
import FormService from '@/services/FormService/FormService';
import ValidationService from '@/services/ValidationService/ValidationService';
import { StyledTextField } from './Input.styles';

const InputTypeToValidationRegExp = {
  [InputType.Integer]: integerValidationPattern,
  [InputType.Number]: numberValidationPattern,
  [InputType.Alphanumeric]: alphanumericRegExp,
  [InputType.AlphanumericAndSymbols]: commonInputValidationPattern,
  [InputType.TIN]: tinValidationPattern,
  [InputType.SSN]: ssnValidationPattern,
  [InputType.Phone]: phoneValidationPattern,
  [InputType.Letters]: lettersRegExp,
  [InputType.AlphanumericAndSpace]: alphanumericRegExpAndSpace,
  [InputType.Decimal]: decimalRegExp,
  [InputType.LettersAndSpace]: lettersAndSpacesRegExp,
};

export type InputProps = Omit<TextFieldProps, 'variant'> & {
  errorMessage?: string;
  typographyVariant?: string;
  maskProps?: MaskProps;
  inputType?: InputType;
  endAdornment?: React.ReactNode;
  dataTestId?: string;
};

export const Input = ({
  errorMessage,
  name,
  maskProps,
  typographyVariant,
  inputType = InputType.AlphanumericAndSymbols,
  inputProps,
  InputProps,
  endAdornment,
  onChange,
  dataTestId,
  ...rest
}: InputProps) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const [isFocused, setIsFocused] = useState(false);

  const textRef = useRef<HTMLDivElement>(null);
  const maskRef = useMask(maskProps);

  const inputRef = maskProps ? maskRef : textRef;

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    onChange?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { value } = event.target;
    if (inputType) {
      const isInputRestricted = ValidationService.shouldRestrictInput(value, InputTypeToValidationRegExp[inputType]);
      if (isInputRestricted) return;
    }
    onChange?.(event);
  };

  const handleBeforeInputChange = (event: React.FormEvent<HTMLDivElement>) => {
    const nativeEvent = event.nativeEvent as InputEvent;
    const inputChar = nativeEvent.data;

    if (inputChar) {
      const isInputRestricted = ValidationService.shouldRestrictInput(
        inputChar,
        InputTypeToValidationRegExp[inputType],
      );
      isInputRestricted && event.preventDefault();
    }
  };

  useLayoutEffect(() => {
    setTimeout(() => {
      if (inputRef.current && FormService.checkIsInputAutofilled(inputRef.current as HTMLInputElement)) {
        FormService.removeAutofillFromTheInput(inputRef.current as HTMLInputElement, () => setIsFocused(true));
      }
    }, 400);
  }, [inputRef]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (inputRef.current && !inputRef.current.contains(event.target as Node)) {
        setIsFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [inputRef]);

  return (
    <StyledTextField
      helperText={errorMessage}
      error={!!errorMessage}
      size={isSmallScreen ? 'small' : 'medium'}
      inputRef={inputRef}
      focused={isFocused}
      onClick={() => setIsFocused(true)}
      InputProps={{
        sx: {
          typography: typographyVariant,
        },
        endAdornment: endAdornment ? <InputAdornment position="end">{endAdornment}</InputAdornment> : null,
        onBeforeInput: handleBeforeInputChange,
        ...InputProps,
      }}
      FormHelperTextProps={{
        id: `error-label-${name}`,
        // @ts-ignore
        'data-testid': `${name}Error`,
      }}
      name={name}
      onChange={(event) => {
        handleChange(event, onChange);
      }}
      inputProps={{
        autoComplete: name?.includes('password') ? 'new-password' : 'off',
        'data-testid': dataTestId || `${name || ''}Input`,
        ...inputProps,
      }}
      {...rest}
      value={rest.value === undefined ? '' : rest.value}
    />
  );
};
